import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import portalApi, {
  errMessage,
  getPortalToken,
  portalPath,
  setPortalToken,
} from "./portalApi";

// Customer portal login (user, 2026-11-05). The company comes from the URL;
// the email + password are the ones set on the customer's contact.
function PortalLogin() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getPortalToken(companyId)) {
      navigate(portalPath(companyId), { replace: true });
      return;
    }
    portalApi
      .get(`/portal/${companyId}/info`)
      .then(({ data }) => setCompany(data?.data))
      .catch(() => setNotFound(true));
  }, [companyId, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await portalApi.post(`/portal/${companyId}/login`, {
        email,
        password,
      });
      setPortalToken(companyId, data.token);
      navigate(portalPath(companyId), { replace: true });
    } catch (err) {
      setError(errMessage(err, "Could not log in"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light px-3">
      <div
        className="card shadow-sm border-0 w-100"
        style={{ maxWidth: 420, borderRadius: 15 }}
      >
        <div className="card-body p-4">
          {notFound ? (
            <div className="text-center py-4">
              <h4>Portal not found</h4>
              <p className="text-muted mb-0">
                Check the link you were sent, or ask your supplier for it.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-4">
                <h4 className="mb-1">{company?.name || " "}</h4>
                <p className="text-muted mb-0">Customer portal</p>
              </div>
              <form onSubmit={submit}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="portal-email">
                    Email
                  </label>
                  <input
                    id="portal-email"
                    className="form-control"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="portal-password">
                    Password
                  </label>
                  <div className="input-group">
                    <input
                      id="portal-password"
                      className="form-control"
                      type={showPass ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPass(!showPass)}
                      aria-label={showPass ? "Hide password" : "Show password"}
                    >
                      <i className={`mdi ${showPass ? "mdi-eye-off" : "mdi-eye"}`}></i>
                    </button>
                  </div>
                </div>
                {error && <div className="alert alert-danger py-2">{error}</div>}
                <button
                  className="btn btn-primary w-100"
                  type="submit"
                  disabled={loading || !company}
                >
                  {loading ? "Logging in..." : "Log in"}
                </button>
                <p className="text-muted small text-center mt-3 mb-0">
                  Forgot your password? Ask your account manager to reset it.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PortalLogin;
